import type { TPageTranslations } from '@crehana/i18n';

import { PAGE_KEY } from '../constants';
import type { TKeys } from './types';

const en: TPageTranslations<TKeys, typeof PAGE_KEY> = {
  [PAGE_KEY]: {
    TITLE_1: 'Hello,',
    TITLE_2: 'What do you need to do today?',
    HEADER_V2_DESCRIPTION:
      'In this space, you will find the main tools for your growth within the company.',
    GREETINGS: 'Welcome, {{name}}',

    TITLE_1_MOBILE: 'Hello',

    CARDS_ENROLLED_COURSES_TITLE: 'Enrolled Courses',
    CARDS_ENROLLED_COURSES_DESCRIPTION:
      'Access the courses assigned to you and continue with your development.',
    CARDS_ENROLLED_COURSES_CTA: 'View my courses',
    CARDS_LEARNING_PATHS_TITLE: 'Learning Paths',
    CARDS_LEARNING_PATHS_DESCRIPTION:
      'Boost your skills with specialized and planned content for you.',
    CARDS_LEARNING_PATHS_CTA: 'View my paths',

    PROFILE_CARD_VIEW_PROFILE: 'View profile',
    PROFILE_CARD_OCCUPATION: 'Occupation',
    PROFILE_CARD_DEPARTMENT: 'Department',
    PROFILE_CARD_BOSS: 'Boss',
    PROFILE_CARD_LOCATION: 'Location',

    TALENT_CARD_TITLE: 'Talent Processes',
    TALENT_CARD_TITLE_SHORT: 'Talent Processes',
    TALENT_CARD_DESCRIPTION:
      'Access guides and tutorials to make the most of courses and learning features.',

    // Sidebar
    SIDEBAR_FOOTER_HELP: 'Help',

    LABEL_SIDEBAR_ITEM_COMEBACK: 'Back to my products',
    LABEL_SIDEBAR_ITEM_COMEBACK_SHORT: 'Back',
    LABEL_SIDEBAR_ITEM_TITLE: 'E-Learning',
    LABEL_SIDEBAR_ITEM_START: 'Start',
    LABEL_SIDEBAR_ITEM_CONTENT_FOR_YOU: 'More content',
    LABEL_SIDEBAR_ITEM_MY_COURSES: 'My Courses',
    LABEL_SIDEBAR_ITEM_MY_COURSES_SEPARATOR: 'LEARNING AND PROGRESS',
    LABEL_SIDEBAR_ITEM_MY_COURSES_ENROLLED: 'Enrolled Courses',
    LABEL_SIDEBAR_ITEM_MY_COURSES_ENROLLED_MOBILE: 'Courses',
    LABEL_SIDEBAR_ITEM_MY_COURSES_SAVED: 'Saved Courses',
    LABEL_SIDEBAR_ITEM_MY_COURSES_TRACKS: 'Learning Paths',
    LABEL_SIDEBAR_ITEM_MY_COURSES_TRACKS_DETAIL: 'Learning Paths of',
    LABEL_SIDEBAR_ITEM_MY_COURSES_SUBTITLE:
      'Explore your assigned paths for personalized learning.',
    LABEL_SIDEBAR_ITEM_MY_COURSES_NOTES: 'Notes',
    LABEL_SIDEBAR_ITEM_MY_COURSES_CERTIFICATIONS: 'Diplomas',
    LABEL_SIDEBAR_ITEM_MY_PROJECTS: 'Projects',
    LABEL_SIDEBAR_ITEM_MY_STUDY_PLAN: 'Study Plan',
    LABEL_SIDEBAR_ITEM_SKILL_XP: 'Skill XP',
    LABEL_SIDEBAR_ITEM_SKILL_XP_EVOLUTION: 'Skill XP Evolution',
    LABEL_SIDEBAR_ITEM_SKILL_XP_EVOLUTION_PENDING: 'Pending Tests',
    LABEL_SIDEBAR_ITEM_SKILL_XP_EVOLUTION_SOLVED: 'Solved Tests',
    LABEL_SIDEBAR_ITEM_SKILL_XP_SEPARATOR: 'ASSESSMENT AND ACHIEVEMENTS',
    LABEL_SIDEBAR_ITEM_MENTORS: 'Mentorship',
    LABEL_SIDEBAR_ITEM_BLOG: 'Blog',
    LABEL_SIDEBAR_ITEM_LIVES: 'Lives',
    LABEL_SIDEBAR_ITEM_TOOLS: 'Tools',
    LABEL_SIDEBAR_ITEM_PLAYROOM_ACHIEVEMENTS: 'Achievements',
    LABEL_SIDEBAR_ITEM_PLAYROOM_REWARDS: 'Rewards',
    LABEL_SIDEBAR_ITEM_PLAYROOM_TAB: 'Gamification',

    SOCIAL_HUB_CARD_TITLE: 'News',
    SOCIAL_HUB_CARD_CTA: 'Read news',
    SOCIAL_HUB_CARD_PUBLISH_DATE: 'Published on {{date}}',
    SOCIAL_HUB_CARD_EMPTY_TITLE: 'Soon your company will publish its news',
    SOCIAL_HUB_CARD_EMPTY_DESCRIPTION:
      'In the meantime, inspire yourself with relevant articles for your professional path.',
    SOCIAL_HUB_CARD_EMPTY_BUTTON: 'Go to Crehana blog',
    SOCIAL_HUB_CARD_ERROR_TITLE: 'An error occurred while loading',
    SOCIAL_HUB_CARD_ERROR_DESCRIPTION: 'Click the button to try loading again.',
    SOCIAL_HUB_CARD_ERROR_CTA: 'Retry',

    LEARNING_CARD_TITLE: 'Continue your learning',

    // Widgets fetching errors
    ERROR_FETCHING_LEARNING_PATHS: 'An error occurred while loading',
    ERROR_FETCHING_COURSES: 'An error occurred while loading',
    ERROR_FETCHING_LAST_ACTIVITY: 'An error occurred while loading',
    GRID_WIDGETS_EMPTY_STATE_TITLE: 'Content could not be loaded',
    GRID_WIDGETS_EMPTY_STATE_DESCRIPTION:
      'There was a problem with the query or the configuration is not recognized for some reason. Please contact your administrator or technical support for a review and to resolve this issue',

    // Error widget translations
    ERROR_WIDGET_TITLE: 'An error occurred',
    ERROR_WIDGET_MESSAGE: 'Click the button to try loading again.',
    ERROR_WIDGET_RETRY_BUTTON: 'Retry',

    USERS: 'Users',
    SEARCH_USER_INPUT_PLACEHOLDER: 'Search',

    HOLIDAY_DETAIL_TABLE_COLUMN_NAME: 'Name',
    HOLIDAY_DETAIL_TABLE_COLUMN_JOB_CATEGORY: 'Job Title',
    HOLIDAY_DETAIL_TABLE_COLUMN_AREA: 'Area',
    HOLIDAY_DETAIL_TABLE_COLUMN_COUNTRY: 'Country',
    HOLIDAY_DETAIL_MODAL_TITLE: 'User Details',
    HOLIDAY_DETAIL_MODAL_INFO:
      'Your area, location and country characteristics do not meet the requirements to access this holiday.',
    HOLIDAY_DETAIL_MODAL_TITLE_DESCRIPTION:
      'View here the users that match the established rule to access this holiday.',
    ERROR_LOADING_DAY_EVENTS: 'Unable to load content',
    ERROR_LOADING_DAY_EVENTS_DESCRIPTION:
      'There was a problem with the query. Please try again later.',
    GO_BACK: 'Go Back',
    RETRY: 'Retry',
    TODO_LIST_ADMIN_WIDGET_TAB_MANAGEMENT: 'Management',
    TODO_LIST_ADMIN_WIDGET_TAB_ALL: 'All',
    DATE_PREPOSITION_DE: '',
    DATE_PREPOSITION_DEL: '',

    // Annual Impact Banner
    ANNUAL_IMPACT_BANNER_TITLE:
      "Discover and recognize what you built in 2025. Because it wasn't just work, it was the mark you left.",
    ANNUAL_IMPACT_BANNER_TITLE_MOBILE: 'Discover your 2025 impact',
    LABEL_ANNUAL_IMPACT_BANNER_BUTTON: 'Discover my impact',

    // Annual Potential Banner
    ANNUAL_POTENTIAL_BANNER_TITLE:
      "2026 is the time to take everything you've built to the next level.",
    ANNUAL_POTENTIAL_BANNER_TITLE_MOBILE: '2026, your next level',
    LABEL_ANNUAL_POTENTIAL_BANNER_BUTTON: 'Unlock my potential',

    // ============ START TIME AND ATTENDANCE TRACKING CARD ============
    // It's an mvp so these keys should be removed later
    today: 'Today',
    check_in: 'Check in',
    lunch_break: 'Lunch break',
    check_out: 'Check out',
    hours_worked: 'Hours worked',
    to_register: 'To register',
    exit: 'Exit',
    start_lunch_break: 'Start lunch break',
    end_lunch_break: 'End lunch break',
    register_lunch_break_toast_title: 'You have registered correctly',
    register_lunch_break_toast_description:
      'You can now check the data in your attendance card.',
    register: 'Register',
    day: 'Day',
    place_of_marking: 'Place of marking',
    register_time: 'Register time',
    scheduled_time: 'Scheduled time',
    delay: 'Delay',
    early_exit_time: 'Early exit time',
    go_to_module: 'Go to module',
    // ============ END TIME AND ATTENDANCE TRACKING CARD ============

    // ============ START PAYROLL RECEIPTS WIDGET ============
    PAYROLL_WIDGET_TITLE: 'Payroll receipts',
    PAYROLL_WIDGET_SUBTITLE: 'View and download your payment receipts',
    PAYROLL_NEXT_PAYMENT_LABEL: 'Next payment date',
    PAYROLL_NEXT_PAYMENT_TOOLTIP:
      'The bank processing times may affect the arrival of the payment.',
    PAYROLL_EMPTY_TITLE: "You don't have any payroll receipts yet",
    PAYROLL_EMPTY_DESCRIPTION:
      'When your company processes payroll, your receipt will appear here.',
    PAYROLL_PERIOD_LABEL: 'Period',
    PAYROLL_VIEW_RECEIPT: 'View receipt',
    PAYROLL_DOWNLOAD: 'Download',
    PAYROLL_DOWNLOAD_RECEIPT_TOOLTIP: 'Download receipt',
    PAYROLL_TOAST_DOWNLOAD_SUCCESS:
      'The receipt in PDF format has been downloaded successfully.',
    PAYROLL_TOAST_DOWNLOAD_ERROR:
      'Sorry, an error occurred while trying to download. Please try again.',
    PAYROLL_TOAST_DOWNLOAD_ERROR_DESCRIPTION: 'Please try again.',

    PAYROLL_ERROR_LOAD_TITLE: 'An error occurred',
    PAYROLL_ERROR_LOAD_DESCRIPTION: 'Click the button to try loading again.',
    PAYROLL_RETRY: 'Retry',

    PAYROLL_MODAL_RECEIPT_TITLE_1: 'Receibo:',
    PAYROLL_MODAL_RECEIPT_TITLE_2:
      'Period {{periodNumber}} from {{start}} to {{end}} of {{month}} {{year}}',
    PAYROLL_MODAL_ERROR_MESSAGE: 'An error occurred',
    PAYROLL_MODAL_ERROR_DESCRIPTION: 'Click the button to try loading again.',
    PAYROLL_MODAL_RETRY: 'Retry',
    PAYROLL_CONTACT_SUPPORT:
      'If the problem persists, contact technical support.',
    // ============ END PAYROLL RECEIPTS WIDGET ============
  },
};

export default en;
